import { useState, useContext } from 'react';
import { FilterContainer, ToggleContainer, ToggleTitle, FilterLabel } from '@/components/Instruments/InstrumentsMainPageStyles';
import { ThemeContext } from '@fluentui/react-theme-provider';
import ManagerService from '@/services/ManagerService';
import { getCurrencies } from '@/services/currencies';
import { getSubcategories } from '@/services/subcategories';
import { useEffect } from 'react';
import { DirectionalHint } from '@fluentui/react';
import ThreeWayToggle from '@/components/ThreeWayToggle/ThreeWayToggle';
import CustomDropdownManager from '@/components/dropdown/CustomDropdownManager';
import { TeachingBubble } from "@fluentui/react";
import { translate } from "@/utils/i18n";
import React from 'react';
import CustomDropdownCategory from '@/components/dropdown/CustomDropdownCategory';

interface DropdownOptionType
{
    key: string;
    label: string;
    value: any;
}
interface SubcategoryType
{
    morningStarCategoryId: string;
    name: string;
}
interface DropdownDataType
{
    managers: DropdownOptionType[];
    currencies: DropdownOptionType[];
    subcategories: DropdownOptionType[];
}
interface SelectedFiltersType
{
    subcategory?: SubcategoryType[];
    manager?: string[];
    currency?: string[];
    available?: boolean;
    income?: boolean;
    switchable?: boolean;
    indexed?: boolean;
    classCategory?: boolean;
}
interface HiddenFiltersType
{
    manager?: boolean;
}
interface FiltersProps
{
    selectedFilters: SelectedFiltersType;
    onChange: (values: any, filterType: FilterType) => void;
    hiddenFilters: HiddenFiltersType;
}
interface DropdownTitleProps
{
    title: string;
}

type FilterType = 'subcategory' | 'manager' | 'currency' | 'available' | 'income' | 'switchable' | 'indexed' | 'classCategory';

const Filters = ({selectedFilters, onChange, hiddenFilters}: FiltersProps) =>
{
    const theme = useContext(ThemeContext);

    // Función helper para convertir valores de filtros a estados de toggle
    const getToggleState = (filterValue: boolean | undefined): number =>
    {
        if (filterValue === true) return 1;   // SÍ
        if (filterValue === false) return 0;  // NO
        return -1;                            // SIN MARCAR
    };

    const [dropdownData, setDropdownData] = useState<DropdownDataType>(
    {
        managers: [],
        currencies: [],
        subcategories: []
    });

    const [loading, setLoading] = useState(false);
    const [tooltipClassToggle, setTooltipClassToggle] = useState(false);
    const [tooltipAvailableToggle, setTooltipAvailableToggle] = useState(false);
    const [tooltipIncomeToggle, setTooltipIncomeToggle] = useState(false);
    const [tooltipSwitchableToggle, setTooltipSwitchableToggle] = useState(false);
    const [tooltipIndexedToggle, setTooltipIndexedToggle] = useState(false);

    const DropdownTitle = ({ title }: DropdownTitleProps): JSX.Element =>
    {
        return <span>{title}</span>;
    };

    useEffect(() =>
    {
        async function populateFilters()
        {
            setLoading(true);
            let managers = await ManagerService.getBQManagers();
            let currencies = await getCurrencies();
            let subcategories = await getSubcategories();

            const convertToItem = (value: any, index: number, array: DropdownOptionType[]) =>
            {
                array[index] =
                {
                    key: value.name || value,
                    label: value.name || value,
                    value: value
                };
            };
            
            const managersOptions: DropdownOptionType[] = [];
            const currenciesOptions: DropdownOptionType[] = [];
            const subcategoriesOptions: DropdownOptionType[] = [];
            
            managers.forEach((item: any, index: number) => convertToItem(item, index, managersOptions));
            currencies.forEach((item: any, index: number) => convertToItem(item, index, currenciesOptions));
            subcategories.forEach((item: any, index: number) => convertToItem(item, index, subcategoriesOptions));

            setDropdownData(
            {
                managers: managersOptions,
                currencies: currenciesOptions,
                subcategories: subcategoriesOptions
            });
            setLoading(false);
        }

        populateFilters();
    }, []);

    return(
        <React.Fragment>
            <FilterContainer>
                <CustomDropdownCategory
                    options={dropdownData.subcategories}
                    title={<DropdownTitle title="Categorías fondo" />}
                    selectedValues={selectedFilters.subcategory}
                    onSave={(value: any) =>
                    {
                        let values = value.map(({ value }: any) =>
                        {
                            return value;
                        });
                        onChange(values, "subcategory");
                    }}
                    isLoading={loading}
                />
                {selectedFilters && selectedFilters.subcategory &&
                    selectedFilters.subcategory.length > 0 &&
                    selectedFilters.subcategory.map((filter: SubcategoryType, filterIndex: number) =>
                    {
                        return <FilterLabel key={"filterIndex" + filterIndex}>{filter.name}</FilterLabel>;
                    })
                }
            </FilterContainer>
            {hiddenFilters.manager === undefined &&
            (
                <FilterContainer>
                    <CustomDropdownManager
                        options={dropdownData.managers}
                        title="Gestora"
                        selectedValues={selectedFilters.manager || []}
                        onSave={(value: any) =>
                        {
                            let values = value.reduce(function (all: any[], current: any)
                            {
                                current = current.value;
                                if (typeof current !== 'string')
                                {
                                    return [...all, ...current];
                                }
                                else
                                {
                                    return [...all, current];
                                }
                            }, []);
                            onChange(values, "manager");
                        }}
                        isLoading={loading}
                    />
                    {selectedFilters && selectedFilters.manager && selectedFilters.manager.length > 0 && Array.from(new Set(selectedFilters.manager.map((m: string) => translate(m)))).map((filter: string, filterIndex: number) =>
                    {
                        return <FilterLabel key={"filterIndex" + filterIndex}>{filter}</FilterLabel>;
                    })}
                </FilterContainer>
            )}
            <FilterContainer>
                <CustomDropdownCategory
                    options={dropdownData.currencies}
                    title={<DropdownTitle title="Divisa" />}
                    selectedValues={selectedFilters.currency}
                    onSave={(value: any) =>
                    {
                        let values = value.map((val: any) =>
                        {
                            val = val.value;
                            return val;
                        });
                        onChange(values, "currency");
                    }}
                    isLoading={loading}
                />
                {selectedFilters && selectedFilters.currency && selectedFilters.currency.length > 0 && selectedFilters.currency.map((filter: string, filterIndex: number) =>
                {
                    return <FilterLabel key={"filterIndex" + filterIndex}>{filter}</FilterLabel>;
                })}
            </FilterContainer>
            <FilterContainer>
                {tooltipAvailableToggle === true &&
                (
                    <TeachingBubble target="#tooltipAvailableToggle" hasCloseButton={false} headline="Filtro de Disponibilidad" onDismiss={() => setTooltipAvailableToggle(false)} calloutProps={{ directionalHint: DirectionalHint.topCenter }}>
                        <h4>
                            {"Disponibilidad: SÍ"}
                        </h4>
                        <div>
                            {"Muestra únicamente fondos que están disponibles para la suscripción."}
                        </div>
                        <h4>
                            {"Disponibilidad: NO"}
                        </h4>
                        <div>
                            {"Muestra únicamente fondos que no están disponibles para la suscripción."}
                        </div>
                        <h4>
                            {"Disponibilidad: SIN MARCAR"}
                        </h4>
                        <div>
                            {"Muestra todos los fondos, independientemente de su disponibilidad para la suscripción (disponibles + no disponibles)."}
                        </div>
                    </TeachingBubble>
                )}
                <div className="ms-Grid-col ms-sm12">
                    <div id="tooltipAvailableToggle" onMouseEnter={() => setTooltipAvailableToggle(true)} onMouseLeave={() => setTooltipAvailableToggle(false)}>
                        <ToggleContainer theme={theme}>
                            <ToggleTitle theme={theme}>Disponibilidad</ToggleTitle>
                            <ThreeWayToggle onChange={(value: any) => onChange(value, "available")} 
                                toggleInitialState={getToggleState(selectedFilters.available)}
                                disableMiddlePosition={false}
                            />
                        </ToggleContainer>
                    </div>
                </div>
            </FilterContainer>
            <FilterContainer>
                {tooltipIncomeToggle === true &&
                (
                    <TeachingBubble target="#tooltipIncomeToggle" hasCloseButton={false} headline="Filtro de Dividendos" onDismiss={() => setTooltipIncomeToggle(false)} calloutProps={{ directionalHint: DirectionalHint.topCenter }}>
                        <h4>
                            {"Dividendos: SÍ"}
                        </h4>
                        <div>
                            {"Muestra únicamente fondos que reparten dividendos o tienen distribución de rentas."}
                        </div>
                        <h4>
                            {"Dividendos: NO"}
                        </h4>
                        <div>
                            {"Muestra únicamente fondos de acumulación que no reparten dividendos."}
                        </div>
                        <h4>
                            {"Dividendos: SIN MARCAR"}
                        </h4>
                        <div>
                            {"Muestra todos los fondos sin filtrar por tipo de dividendo."}
                        </div>
                    </TeachingBubble>
                )}
                <div className="ms-Grid-col ms-sm12">
                    <div id="tooltipIncomeToggle" onMouseEnter={() => setTooltipIncomeToggle(true)} onMouseLeave={() => setTooltipIncomeToggle(false)}>
                        <ToggleContainer theme={theme}>
                            <ToggleTitle theme={theme}>Dividendos</ToggleTitle>
                            <ThreeWayToggle onChange={(value: any) => onChange(value, "income")} 
                                toggleInitialState={getToggleState(selectedFilters.income)}
                                disableMiddlePosition={false}
                            />
                        </ToggleContainer>
                    </div>
                </div>
            </FilterContainer>
            <FilterContainer>
                {tooltipSwitchableToggle === true &&
                (
                    <TeachingBubble target="#tooltipSwitchableToggle" hasCloseButton={false} headline="Filtro de Traspasable" onDismiss={() => setTooltipSwitchableToggle(false)} calloutProps={{ directionalHint: DirectionalHint.topCenter }}>
                        <h4>
                            {"Traspasable: SÍ"}
                        </h4>
                        <div>
                            {"Muestra únicamente fondos que permiten traspasos a otros fondos de la misma gestora."}
                        </div>
                        <h4>
                            {"Traspasable: NO"}
                        </h4>
                        <div>
                            {"Muestra únicamente fondos que no permiten realizar traspasos."}
                        </div>
                        <h4>
                            {"Traspasable: SIN MARCAR"}
                        </h4>
                        <div>
                            {"Muestra todos los fondos sin filtrar por la capacidad de traspaso."}
                        </div>
                    </TeachingBubble>
                )}
                <div className="ms-Grid-col ms-sm12">
                    <div id="tooltipSwitchableToggle" onMouseEnter={() => setTooltipSwitchableToggle(true)} onMouseLeave={() => setTooltipSwitchableToggle(false)}>
                        <ToggleContainer theme={theme}>
                            <ToggleTitle theme={theme}>Traspasable</ToggleTitle>
                            <ThreeWayToggle onChange={(value: any) => onChange(value, "switchable")} 
                                toggleInitialState={getToggleState(selectedFilters.switchable)}
                                disableMiddlePosition={false}
                            />
                        </ToggleContainer>
                    </div>
                </div>
            </FilterContainer>
            <FilterContainer>
                {tooltipIndexedToggle === true &&
                (
                    <TeachingBubble target="#tooltipIndexedToggle" hasCloseButton={false} headline="Filtro de Indexado" onDismiss={() => setTooltipIndexedToggle(false)} calloutProps={{ directionalHint: DirectionalHint.topCenter }}>
                        <h4>
                            {"Indexado: SÍ"}
                        </h4>
                        <div>
                            {"Muestra únicamente fondos indexados."}
                        </div>
                        <h4>
                            {"Indexado: NO"}
                        </h4>
                        <div>
                            {"Muestra únicamente fondos de gestión activa que no siguen un índice específico."}
                        </div>
                        <h4>
                            {"Indexado: SIN MARCAR"}
                        </h4>
                        <div>
                            {"Muestra todos los fondos sin filtrar por tipo de gestión (indexados + gestión activa)."}
                        </div>
                    </TeachingBubble>
                )}
                <div className="ms-Grid-col ms-sm12">
                    <div id="tooltipIndexedToggle" onMouseEnter={() => setTooltipIndexedToggle(true)} onMouseLeave={() => setTooltipIndexedToggle(false)}>
                        <ToggleContainer theme={theme}>
                            <ToggleTitle theme={theme}>Indexado</ToggleTitle>
                            <ThreeWayToggle onChange={(value: any) => onChange(value, "indexed")} 
                                toggleInitialState={getToggleState(selectedFilters.indexed)}
                                disableMiddlePosition={false}
                            />
                        </ToggleContainer>
                    </div>
                </div>
            </FilterContainer>
            <FilterContainer>
                {tooltipClassToggle === true &&
                (
                    <TeachingBubble target="#tooltipClassToggle" hasCloseButton={false} headline="Filtro de Clases" onDismiss={() => setTooltipClassToggle(false)} calloutProps={{ directionalHint: DirectionalHint.topCenter }}>
                        <h4>
                            {"Clases: SÍ"}
                        </h4>
                        <div>
                            {"Devuelve todas las clases de un fondo si una clase cumple con los criterios de búsqueda."}
                        </div>
                        <h4>
                            {"Clases: NO"}
                        </h4>
                        <div>
                            {"Devuelve solo las clases que cumplen los criterios de búsqueda."}
                        </div>
                        <h4>
                            {"Clases: SIN MARCAR"}
                        </h4>
                        <div>
                            {"Comportamiento por defecto del sistema sin aplicar filtrado específico de clases."}
                        </div>
                    </TeachingBubble>
                )}
                <div className="ms-Grid-col ms-sm12">
                    <div id="tooltipClassToggle" onMouseEnter={() => setTooltipClassToggle(true)} onMouseLeave={() => setTooltipClassToggle(false)}>
                        <ToggleContainer theme={theme}>
                            <ToggleTitle theme={theme}>Clases</ToggleTitle>
                            <ThreeWayToggle toggleInitialState={getToggleState(selectedFilters.classCategory)} disableMiddlePosition={true} onChange={(value: any) => onChange(value, "classCategory")} />
                        </ToggleContainer>
                    </div>
                </div>
            </FilterContainer>
        </React.Fragment>
    );
};

export default Filters;